import { useCallback, useState } from "react"
import { useChannel } from "@/hooks/use-channel"
import type { VoiceCallRecord } from "@/types/v2/voice"

// Subscribes to a single call's post-processing stream
// (`voice_call:<id>`) and keeps the latest VoiceCall record
// in state. Backs the transcript / summary / insights
// panels on the deal & contact show pages.
//
// Events (from `V2::Voice::Broadcasts`, on the
// `voice_call:<id>` stream):
//   - transcript.ready → transcript + status land
//   - insights.ready   → summary + insights land
//   - analysis.failed  → transcript/insights status flip to
//                        the terminal "failed" so the UI can
//                        stop spinning "Analyzing call…"
//
// `useChannel` camelizes KEYS, not VALUES — so the event
// discriminator stays snake/dotted (`transcript.ready`),
// and the nested `voiceCall` payload arrives camelCased,
// matching `VoiceCallRecord`.
export function useVoiceCallChannel(
  initial: VoiceCallRecord,
): VoiceCallRecord {
  const [voiceCall, setVoiceCall] = useState<VoiceCallRecord>(initial)
  // Adopt a changed `initial` (a new call after a redial, a
  // fresher record synced by the softphone, or fresh server
  // props after a partial reload). Without this the hook served
  // the mount-time record forever — remounting the after-call
  // card reset it to a stale snapshot and re-showed
  // "Analyzing call…" after the broadcasts had already fired
  // (CON-211).
  const [prevInitial, setPrevInitial] = useState(initial)
  if (initial !== prevInitial) {
    setPrevInitial(initial)
    setVoiceCall(initial)
  }

  const onReceived = useCallback(
    (data: Record<string, unknown>) => {
      const type = data.type
      if (
        type !== "transcript.ready" &&
        type !== "insights.ready" &&
        type !== "analysis.failed"
      ) {
        return
      }
      const next = data.voiceCall as VoiceCallRecord | undefined
      if (next && next.id === initial.id) {
        setVoiceCall(next)
      }
    },
    [initial.id],
  )

  useChannel({
    channel: "V2::VoiceChannel",
    params: { voice_call_id: initial.id },
    onReceived,
  })

  return voiceCall
}
